<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import NucleoWorkOrdersWelcome from '../components/NucleoWorkOrdersWelcome.vue';
import NucleoWorkOrdersFilters from '../components/NucleoWorkOrdersFilters.vue';
import NucleoWorkOrdersTable from '../components/NucleoWorkOrdersTable.vue';
import NucleoWorkOrdersTabs from '../components/NucleoWorkOrdersTabs.vue';
import NucleoWorkOrderCompletionFeedback from '../components/NucleoWorkOrderCompletionFeedback.vue';
import { useNucleoWorkOrders } from '../composables/useNucleoWorkOrders';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const filtersVisible = ref(true);
const hiddenChangeLog = ref(
  window.localStorage.getItem('hidden_change_log') === 'true'
);
const currentTime = ref(Date.now());
let relativeTimeInterval;
const queue = useNucleoWorkOrders({ notify: useAlert, t });
const completionFeedbackVisible = ref(
  route.query?.feedback === 'finalizar-pedido'
);
const firstName = computed(
  () => currentUser.value?.name?.split(' ')?.[0] || ''
);
const lastUpdatedLabel = computed(() => {
  if (!queue.lastUpdatedAt.value) return '';

  const elapsedMinutes = Math.max(
    0,
    Math.floor(
      (currentTime.value - queue.lastUpdatedAt.value.getTime()) / 60000
    )
  );

  return t('NUCLEO_WORK_ORDERS.LAST_UPDATED', { minutes: elapsedMinutes });
});
const dismissChangeLog = () => {
  hiddenChangeLog.value = true;
  window.localStorage.setItem('hidden_change_log', 'true');
};
const dismissCompletionFeedback = () => {
  completionFeedbackVisible.value = false;
};
const openRow = async row => {
  const workOrderId =
    row.selectedServiceId || row.id_relacao_status || row.servicos?.[0]?.id;

  try {
    await queue.markViewed(row.imovel_listing_id, workOrderId);
  } catch {
    return;
  }

  router.push({
    name: 'operacao_imobiliaria_nucleo_detalhe',
    params: {
      accountId: route.params.accountId,
      imovelListingId: row.imovel_listing_id,
      workOrderId,
    },
  });
};

onMounted(() => {
  if (route.query?.feedback === 'finalizar-pedido') {
    router
      .replace({ query: { ...route.query, feedback: undefined } })
      .catch(() => undefined);
  }
  relativeTimeInterval = window.setInterval(() => {
    currentTime.value = Date.now();
  }, 60000);
});

onBeforeUnmount(() => {
  window.clearInterval(relativeTimeInterval);
});
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('NUCLEO_WORK_ORDERS.TITLE')"
      :subtitle="t('NUCLEO_WORK_ORDERS.SUBTITLE')"
      :breadcrumb="[{ label: t('NUCLEO_WORK_ORDERS.TITLE') }]"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-list-filter"
          @click="filtersVisible = !filtersVisible"
        >
          {{ t('NUCLEO_WORK_ORDERS.ACTIONS.FILTER') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="queue.loading.value || queue.loadingCatalog.value"
          @click="queue.refresh"
        >
          {{ t('NUCLEO_WORK_ORDERS.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
        <span
          v-if="lastUpdatedLabel"
          class="whitespace-nowrap text-xs text-lais-text-muted"
        >
          {{ lastUpdatedLabel }}
        </span>
      </template>
    </LaisHeader>
    <main
      class="mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <NucleoWorkOrdersWelcome
        v-if="!hiddenChangeLog"
        :first-name="firstName"
        :new-services="queue.counts.value.novos || 0"
        @dismiss="dismissChangeLog"
      />
      <NucleoWorkOrdersTabs
        :model-value="queue.tab.value"
        :counts="queue.counts.value"
        @update:model-value="queue.setTab"
      />
      <NucleoWorkOrdersFilters
        v-if="filtersVisible"
        :filters="queue.filters.value"
        :catalog="queue.catalog.value"
        :loading="queue.loading.value || queue.loadingCatalog.value"
        @apply="queue.applyFilters"
        @clear="queue.clearFilters"
      />
      <p
        v-if="queue.error.value"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ queue.error.value }}
      </p>
      <NucleoWorkOrdersTable
        :rows="queue.rows.value"
        :loading="queue.loading.value"
        :page="queue.page.value"
        :total-pages="queue.totalPages.value"
        :total-rows="queue.meta.value.count"
        @change-page="queue.changePage"
        @open="openRow"
      />
    </main>
    <NucleoWorkOrderCompletionFeedback
      :show="completionFeedbackVisible"
      @close="dismissCompletionFeedback"
    />
  </div>
</template>
